import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Alert,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)', borderRadius: 0 },
    '&:hover fieldset': { borderColor: '#3a6eb6' },
    '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
    '&.Mui-disabled fieldset': { borderColor: 'rgba(255, 255, 255, 0.06)' },
  },
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(203, 213, 225, 0.38)' },
};

const selectSx = {
  borderRadius: 0,
  color: '#cbd5e1',
  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)', borderRadius: 0 },
  '&:hover fieldset': { borderColor: '#3a6eb6' },
  '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
  '&.Mui-disabled': { color: 'rgba(203, 213, 225, 0.38)' },
  '& .MuiSvgIcon-root': { color: '#94a3b8' },
};

const labelSx = { style: { color: '#94a3b8' } };
const inputSx = { style: { color: '#cbd5e1' } };

const INITIAL_STATE = {
  apellido: '',
  nombres: '',
  documento: '',
  fideId: '',
  ratingFide: '',
  grau: '',
  grauMode: 'noRankeado',
  coeficienteK: '',
};

function calcularGrau(ratingFide) {
  const fide = parseFloat(ratingFide);
  if (isNaN(fide)) return '';
  return String(Math.round((fide * 1.12) - 42));
}

export default function QuickRegisterDialog({ open, onClose, onSubmit, loading }) {
  const [data, setData] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [fideLoading, setFideLoading] = useState(false);
  const [fideError, setFideError] = useState('');

  // ── Helpers ──────────────────────────────────────────────────────────────

  const clearError = (name) => {
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleGrauModeChange = (e) => {
    const mode = e.target.value;
    setData((prev) => {
      let grau = prev.grau;
      let coeficienteK = prev.coeficienteK;

      if (mode === 'noRankeado') {
        grau = '0';
        coeficienteK = '';
      } else if (mode === 'calcularFide') {
        grau = prev.ratingFide ? calcularGrau(prev.ratingFide) : '';
        // keep coeficienteK as-is
      }
      // 'manual': no changes to grau or K

      return { ...prev, grauMode: mode, grau, coeficienteK };
    });
    setErrors((prev) => ({ ...prev, grau: null, coeficienteK: null }));
  };

  const handleRatingFideChange = (e) => {
    const value = e.target.value;
    setData((prev) => {
      const grau = prev.grauMode === 'calcularFide' ? calcularGrau(value) : prev.grau;
      return { ...prev, ratingFide: value, grau };
    });
    clearError('ratingFide');
  };

  // ── FIDE Import ───────────────────────────────────────────────────────────

  // Helper: parse JSON safely, throwing a readable error if the response is HTML
  const parseJsonOrThrow = async (res) => {
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new Error(
        'El servidor no está disponible. Asegurate de que el backend esté corriendo.',
      );
    }
    return res.json();
  };

  const handleImportFide = async () => {
    if (!data.fideId.trim()) return;
    setFideLoading(true);
    setFideError('');
    try {
      const res = await fetch(`/api/fide/profile/${data.fideId.trim()}`);
      if (!res.ok) {
        const body = await parseJsonOrThrow(res).catch(() => ({}));
        throw new Error(body?.message || 'Error al consultar FIDE');
      }
      const profile = await parseJsonOrThrow(res);

      setData((prev) => {
        // Parse "Apellido, Nombre" from FIDE
        let apellido = prev.apellido;
        let nombres = prev.nombres;
        if (profile.nombre) {
          const parts = profile.nombre.split(',');
          apellido = parts[0]?.trim() || prev.apellido;
          nombres = parts[1]?.trim() || prev.nombres;
        }

        const ratingFide = profile.ratingStandard != null ? String(profile.ratingStandard) : prev.ratingFide;
        const grau = prev.grauMode === 'calcularFide' && ratingFide
          ? calcularGrau(ratingFide)
          : prev.grau;

        return { ...prev, apellido, nombres, ratingFide, grau };
      });

      if (profile.ratingStandard == null) {
        setFideError('El jugador no tiene rating Standard FIDE. No es posible calcular el GRAU desde FIDE.');
      }
    } catch (err) {
      setFideError(err.message || 'No se pudo obtener el perfil FIDE.');
    } finally {
      setFideLoading(false);
    }
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validate = () => {
    const temp = {};
    if (!data.apellido.trim()) temp.apellido = 'El apellido es obligatorio';
    if (!data.nombres.trim()) temp.nombres = 'El nombre es obligatorio';

    if (data.grauMode !== 'noRankeado') {
      const grauNum = parseFloat(data.grau);
      if (data.grau === '' || isNaN(grauNum)) {
        temp.grau = 'El GRAU es obligatorio para el modo seleccionado';
      }
      if (!data.coeficienteK) {
        temp.coeficienteK = 'El coeficiente K es obligatorio';
      }
    }

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  // ── Build payload ─────────────────────────────────────────────────────────

  const buildPayload = () => {
    const isRankeado = data.grauMode !== 'noRankeado';

    // Map K value: K25→0, K15→1, K10→2
    let coeficienteKCodificado = 0;
    if (isRankeado && data.coeficienteK) {
      if (data.coeficienteK === '10') coeficienteKCodificado = 2;
      else if (data.coeficienteK === '15') coeficienteKCodificado = 1;
      else coeficienteKCodificado = 0; // K25
    }

    return {
      apellido: data.apellido.trim(),
      nombres: data.nombres.trim(),
      documento: data.documento.trim() || undefined,
      fideId: data.fideId.trim() || undefined,
      grau: isRankeado ? parseFloat(data.grau) || 0 : 0,
      coeficienteK: coeficienteKCodificado,
      estadoElo: isRankeado ? 1 : 0,
    };
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(buildPayload(), () => {
      setData(INITIAL_STATE);
      setErrors({});
      setFideError('');
    });
  };

  const handleClose = () => {
    setData(INITIAL_STATE);
    setErrors({});
    setFideError('');
    onClose();
  };

  // ── Derived state ─────────────────────────────────────────────────────────

  const isNoRankeado = data.grauMode === 'noRankeado';
  const isCalcFide = data.grauMode === 'calcularFide';
  const ratingFideNum = parseFloat(data.ratingFide);
  const showRatingWarning = isCalcFide && data.ratingFide !== '' && !isNaN(ratingFideNum) && ratingFideNum < 1800;
  const fideIdPopulated = data.fideId.trim() !== '';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 0,
          background: '#1e293b',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          color: '#cbd5e1',
          width: '100%',
          maxWidth: '500px',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 1.5, fontWeight: 600 }}>
        Registrar Nuevo Jugador
      </DialogTitle>

      <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          El jugador se creará en el sistema y se inscribirá automáticamente en este torneo.
        </Typography>

        {/* Apellido */}
        <TextField
          required
          fullWidth
          label="Apellido"
          name="apellido"
          value={data.apellido}
          onChange={handleChange}
          error={!!errors.apellido}
          helperText={errors.apellido}
          InputLabelProps={labelSx}
          inputProps={inputSx}
          sx={fieldSx}
        />

        {/* Nombres */}
        <TextField
          required
          fullWidth
          label="Nombres"
          name="nombres"
          value={data.nombres}
          onChange={handleChange}
          error={!!errors.nombres}
          helperText={errors.nombres}
          InputLabelProps={labelSx}
          inputProps={inputSx}
          sx={fieldSx}
        />

        {/* DNI */}
        <TextField
          fullWidth
          label="DNI / Documento"
          name="documento"
          value={data.documento}
          onChange={handleChange}
          InputLabelProps={labelSx}
          inputProps={inputSx}
          sx={fieldSx}
        />

        {/* FIDE ID + botón importar */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            label="FIDE ID (opcional)"
            name="fideId"
            value={data.fideId}
            onChange={handleChange}
            InputLabelProps={labelSx}
            inputProps={inputSx}
            sx={fieldSx}
          />
          <Tooltip title={fideIdPopulated ? 'Importar desde FIDE' : 'Ingrese un FIDE ID primero'}>
            <span>
              <Button
                variant="outlined"
                onClick={handleImportFide}
                disabled={!fideIdPopulated || fideLoading}
                sx={{
                  minWidth: 48,
                  height: 56,
                  borderRadius: 0,
                  borderColor: fideIdPopulated ? '#3a6eb6' : 'rgba(255,255,255,0.15)',
                  color: fideIdPopulated ? '#3a6eb6' : '#64748b',
                  flexShrink: 0,
                  '&:hover': { borderColor: '#5a8ed6', background: 'rgba(58,110,182,0.08)' },
                  '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.2)' },
                }}
              >
                {fideLoading ? <CircularProgress size={18} color="inherit" /> : <CloudDownloadIcon fontSize="small" />}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Error/info del import FIDE */}
        {fideError && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: 0,
              background: 'rgba(234,179,8,0.1)',
              border: '1px solid rgba(234,179,8,0.3)',
              color: '#fbbf24',
              '& .MuiAlert-icon': { color: '#fbbf24' },
              py: 0.5,
            }}
          >
            {fideError}
          </Alert>
        )}

        {/* Rating FIDE */}
        <TextField
          fullWidth
          label="Rating FIDE"
          name="ratingFide"
          value={data.ratingFide}
          onChange={handleRatingFideChange}
          InputLabelProps={labelSx}
          inputProps={{ ...inputSx, type: 'number' }}
          sx={fieldSx}
          helperText="Solo para referencia — no se guarda en la base de datos"
          FormHelperTextProps={{ sx: { color: '#64748b' } }}
        />

        {/* GRAU + selector de modo */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <TextField
            label="GRAU"
            name="grau"
            value={data.grau}
            onChange={handleChange}
            disabled={isNoRankeado}
            error={!!errors.grau}
            helperText={errors.grau}
            InputLabelProps={labelSx}
            inputProps={{ ...inputSx, type: 'number' }}
            sx={{ ...fieldSx, flex: 1 }}
          />
          <FormControl sx={{ flex: 1.6 }}>
            <InputLabel sx={{ color: '#94a3b8', '&.Mui-focused': { color: '#94a3b8' } }}>
              Modo GRAU
            </InputLabel>
            <Select
              value={data.grauMode}
              onChange={handleGrauModeChange}
              label="Modo GRAU"
              sx={selectSx}
              MenuProps={{
                PaperProps: {
                  sx: {
                    background: '#1e293b',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 0,
                    '& .MuiMenuItem-root': { color: '#cbd5e1' },
                    '& .MuiMenuItem-root:hover': { background: 'rgba(58,110,182,0.15)' },
                    '& .MuiMenuItem-root.Mui-selected': { background: 'rgba(58,110,182,0.25)' },
                  },
                },
              }}
            >
              <MenuItem value="noRankeado">No rankeado</MenuItem>
              <MenuItem value="calcularFide">Calcular desde FIDE</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Advertencia rating FIDE < 1800 */}
        {showRatingWarning && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: 0,
              background: 'rgba(234,179,8,0.1)',
              border: '1px solid rgba(234,179,8,0.3)',
              color: '#fbbf24',
              '& .MuiAlert-icon': { color: '#fbbf24' },
              py: 0.5,
              mt: -1.5,
            }}
          >
            La conversión FIDE→GRAU es confiable solo para ratings ≥ 1800. El valor calculado puede no ser preciso.
          </Alert>
        )}

        {/* Coeficiente K */}
        <FormControl disabled={isNoRankeado} error={!!errors.coeficienteK}>
          <InputLabel sx={{ color: '#94a3b8', '&.Mui-focused': { color: '#94a3b8' } }}>
            Coeficiente K {!isNoRankeado && '*'}
          </InputLabel>
          <Select
            value={data.coeficienteK}
            onChange={(e) => {
              setData((prev) => ({ ...prev, coeficienteK: e.target.value }));
              clearError('coeficienteK');
            }}
            label={`Coeficiente K ${!isNoRankeado ? '*' : ''}`}
            sx={selectSx}
            MenuProps={{
              PaperProps: {
                sx: {
                  background: '#1e293b',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 0,
                  '& .MuiMenuItem-root': { color: '#cbd5e1' },
                  '& .MuiMenuItem-root:hover': { background: 'rgba(58,110,182,0.15)' },
                  '& .MuiMenuItem-root.Mui-selected': { background: 'rgba(58,110,182,0.25)' },
                },
              },
            }}
          >
            <MenuItem value="25">K = 25</MenuItem>
            <MenuItem value="15">K = 15</MenuItem>
            <MenuItem value="10">K = 10</MenuItem>
          </Select>
          {errors.coeficienteK && <FormHelperText>{errors.coeficienteK}</FormHelperText>}
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.08)', gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            color: '#94a3b8',
            borderColor: 'rgba(255, 255, 255, 0.15)',
            borderRadius: 0,
            '&:hover': {
              borderColor: '#cbd5e1',
              background: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            background: '#3a6eb6',
            borderRadius: 0,
            '&:hover': { background: '#2a5793' },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Registrar e Inscribir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
